<template>
    <div>
        <v-autocomplete
                v-model="carModel"
                :items="items"
                :label="label"
                :loading="loading"
                :search-input.sync="search"
                full-width
                clearable
                no-filter
                item-value="id"
                item-text="text"
                color="primary"
                :placeholder="$t('camerasPage.searchCarHint')"
                :no-data-text="noDataText"
                prepend-icon="mdi-database-search"
        ></v-autocomplete>
        <div v-if="failed" class="red--text">{{ $t("camerasPage.carsLoadFailed") }}</div>
    </div>
</template>

<script>
    // Car picker for the camera↔car binding. A camera is bound to a CAR, so
    // the search runs against the fleet directly — an earlier version searched
    // a captain first and then listed his cars, which meant the whole binding
    // died on one missing endpoint and reported "this captain has no car" for
    // every driver in the system.
    //
    // The backend matches the plate, the brand/model and the driver's name and
    // phone, so a plate and a driver's name both find the car without the
    // picker ever asking the admin which one they meant.
    //
    // Emits the car id only on a real change, so DataTable's changed-fields
    // diff never sends car_id unless the admin actually re-binds.
    const SEARCH_DEBOUNCE_MS = 300;
    const SEARCH_LIMIT = 50;

    export default {
        name: "choose-car-single",
        props: {
            label: String,
            value: [Number, String],
        },
        data() {
            return {
                loading: false,
                failed: false,
                searched: false,
                carModel: null,
                search: null,
                cars: [],
                // The car currently chosen, kept apart from the search
                // results: the next search replaces `cars`, and an option that
                // disappears takes the field's own text with it.
                selectedCar: null,
                // Set for exactly one watcher run: filling the field with the
                // binding the camera already holds is not a re-bind, and must
                // not put car_id into DataTable's changed-fields diff.
                initialising: false,
                // Once the admin has touched the field, the binding the dialog
                // opened with is history: a by-id resolve that lands late must
                // not paint that car back over their choice.
                touched: false,
                // Monotonic request number: only the newest request may write
                // to `cars`. Without it a slow earlier search (or the mount
                // resolve) lands last and replaces a newer result set -- and a
                // late failure would wipe a good list.
                requestSeq: 0,
                debounce: null,
            }
        },
        computed: {
            items() {
                const options = this.cars.map(car => ({id: car.id, text: this.carLabel(car)}));
                if (this.selectedCar && !this.cars.some(car => car.id === this.selectedCar.id))
                    options.unshift({id: this.selectedCar.id, text: this.carLabel(this.selectedCar)});
                return options;
            },
            noDataText() {
                if (this.failed) return this.$t("camerasPage.carsLoadFailed");
                if (this.loading) return this.$t("camerasPage.carsSearching");
                return this.searched ? this.$t("camerasPage.noCarMatches") : this.$t("camerasPage.searchCarHint");
            },
        },
        methods: {
            carLabel(car) {
                const plate = car.plate_number || car.label || ("#" + car.id);
                return [plate, car.captain_name, car.captain_phone].filter(Boolean).join(" — ");
            },
            fetchCars(params, onLoaded) {
                const seq = ++this.requestSeq;
                this.loading = true;
                this.failed = false;
                this.axios.get(this.api.ACTIONS.CARS, {params})
                    .then(({data}) => {
                        if (seq !== this.requestSeq) return;
                        const cars = (data && data.data) || [];
                        this.cars = cars instanceof Array ? cars : [cars].filter(Boolean);
                        if (onLoaded) onLoaded(this.cars);
                    })
                    .catch(error => {
                        if (seq !== this.requestSeq) return;
                        // A swallowed failure is indistinguishable from an
                        // empty fleet: the admin reads "no data" as "the car is
                        // not in the system" and goes looking in the wrong
                        // place. Say the request failed instead.
                        this.cars = [];
                        this.failed = true;
                        const message = error && error.response && error.response.data && error.response.data.message;
                        window.getApp.$emit("SHOW_TOAST", message || this.$t("camerasPage.carsLoadFailed"), "red");
                    })
                    .finally(() => {
                        if (seq === this.requestSeq) this.loading = false;
                    });
            },
        },
        watch: {
            search(val) {
                if (!val) return;
                // v-autocomplete writes the selected item's own text back into
                // search-input; searching for it again would be pointless.
                if (this.items.some(item => item.text === val)) return;
                clearTimeout(this.debounce);
                this.debounce = setTimeout(() => {
                    this.searched = true;
                    this.fetchCars({search: val, limit: SEARCH_LIMIT});
                }, SEARCH_DEBOUNCE_MS);
            },
            carModel(val) {
                this.selectedCar = this.cars.find(car => String(car.id) === String(val)) || (val ? this.selectedCar : null);
                if (this.initialising) {
                    this.initialising = false;
                    return;
                }
                this.touched = true;
                // Empty string (not null): FormData would send null as the
                // literal string "null"; '' becomes a real NULL server-side
                // via Laravel's ConvertEmptyStringsToNull.
                this.$emit('input', val || '');
            },
        },
        mounted() {
            if (!this.value) return;
            // Resolve the existing binding by id: the fleet is longer than one
            // page of search results, so it cannot be assumed to be in a list.
            this.fetchCars({id: this.value}, cars => {
                // Clearing the field is a choice too, and it emits before this
                // can land -- painting the old car back would show one car and
                // save another.
                if (this.touched) return;
                if (!cars.length || cars[0].id === this.carModel) return;
                this.selectedCar = cars[0];
                this.initialising = true;
                this.carModel = cars[0].id;
            });
        },
        beforeDestroy() {
            clearTimeout(this.debounce);
        },
    }
</script>
